<template>
  <div class="qhls-exam-center-list">
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border text-primary" role="status"></div>
    </div>

    <template v-else>
      <div class="page-header">
        <h3 class="page-title mal-text">QHLS പരീക്ഷാ കേന്ദ്രങ്ങൾ (Exam Centers List)</h3>
        <p class="page-subtitle mal-text">
          സ്റ്റേറ്റ് / ജില്ലാ / സോൺ അഡ്മിൻ കാണാവുന്ന QHLS പരീക്ഷാ കേന്ദ്രങ്ങളുടെ പട്ടിക
        </p>
      </div>

      <div class="readonly-banner mal-text">
        യൂണിറ്റുകളിൽ ചേർത്ത എല്ലാ QHLS പരീക്ഷാ കേന്ദ്രങ്ങളും ഇവിടെ നിന്നും കാണാം.
      </div>

      <div v-if="savedCenters.length > 0" class="saved-centers-section">
        <h4 class="section-title mal-text">പരീക്ഷാ കേന്ദ്രങ്ങൾ</h4>
        <div class="grouped-toolbar">
          <div class="grouped-title mal-text">{{ groupHeading }}</div>
          <div class="toolbar-actions">
            <button
              v-if="canExportExcel"
              type="button"
              class="btn btn-sm btn-success"
              @click="exportExcel"
              :disabled="excelDownloading"
            >
              {{ excelDownloading ? 'Exporting...' : 'Export Excel' }}
            </button>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="toggleGrouped">
              {{ groupedMode ? 'Show List' : 'Show Summary' }}
            </button>
          </div>
        </div>

        <!-- Summary -->
        <div v-if="groupedMode" class="grouped-list">
          <div class="group-detail-header" v-if="canGoBack || (drillShowCenters && selectedUnitKey)">
            <div class="group-detail-title mal-text">
              <template v-if="drillShowCenters && selectedUnitKey">
                {{ selectedUnitLabel }}
              </template>
              <template v-else>
                {{ drillTitle }}
              </template>
            </div>
            <button type="button" class="btn btn-sm btn-secondary" @click="drillBack">Back</button>
          </div>

          <!-- Cards: District / Zone / Unit summary -->
          <template v-if="!(drillShowCenters && selectedUnitKey)">
            <div
              v-for="g in drillCards"
              :key="g.key"
              class="group-card"
              @click="drillSelect(g)"
              role="button"
            >
              <div class="group-left">
                <div class="group-name">{{ g.label }}</div>
              </div>
              <div class="group-right">
                <div class="group-count">{{ g.count }}</div>
                <div class="group-count-label mal-text">centers</div>
              </div>
            </div>
          </template>

          <!-- Centers list: shown after selecting a unit -->
          <div v-if="drillShowCenters && selectedUnitKey" class="group-detail">
            <div class="saved-centers-list">
              <div v-for="(center, idx) in filteredCenters" :key="center.id || idx" class="center-row">
                <div class="center-index">
                  {{ idx + 1 }}
                </div>

                <div class="saved-center-card">
                  <div class="center-info">
                    <div class="center-header">
                      <strong class="center-name">{{ getCenterName(center) }}</strong>
                    </div>

                    <div class="meta-grid">
                      <div class="meta-row" v-if="getZoneName(center)">
                        <span class="meta-label mal-text">സോൺ</span>
                        <span class="meta-value">{{ getZoneName(center) }}</span>
                      </div>
                      <div class="meta-row" v-if="getUnitName(center)">
                        <span class="meta-label mal-text">യൂണിറ്റ്</span>
                        <span class="meta-value">{{ getUnitName(center) }}</span>
                      </div>
                      <div class="meta-row" v-if="showStateWideDistrictColumn && center.district_name">
                        <span class="meta-label mal-text">ജില്ല</span>
                        <span class="meta-value">{{ center.district_name }}</span>
                      </div>
                    </div>

                    <div class="divider"></div>

                    <div class="meta-grid">
                      <div class="meta-row" v-if="center.convenor_name">
                        <span class="meta-label mal-text">കൺവീനർ</span>
                        <span class="meta-value">{{ center.convenor_name }}</span>
                      </div>
                      <div class="meta-row" v-if="center.invigilator_name">
                        <span class="meta-label mal-text">ഇൻവിജിലേറ്റർ</span>
                        <span class="meta-value">{{ center.invigilator_name }}</span>
                      </div>
                      <div class="meta-row">
                        <span class="meta-label mal-text">ഫോൺ</span>
                        <span class="meta-value">
                          <a v-if="center.convenor_phone" :href="'tel:' + center.convenor_phone">{{ center.convenor_phone }}</a>
                          <template v-else>-</template>
                        </span>
                      </div>
                      <div class="meta-row" v-if="center.exam_time">
                        <span class="meta-label mal-text">സമയം</span>
                        <span class="meta-value">{{ center.exam_time || '' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- List -->
        <div v-else class="saved-centers-list">
          <div v-for="(center, idx) in savedCenters" :key="center.id || idx" class="center-row">
            <div class="center-index">
              {{ idx + 1 }}
            </div>

            <div class="saved-center-card">
              <div class="center-info">
                <div class="center-header">
                  <strong class="center-name">{{ getCenterName(center) }}</strong>
                  <div class="center-actions" v-if="isInvigilatorForCenter(center)">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-success"
                      @click="exportCenterCandidatesExcel(center)"
                      :disabled="isCenterExcelDownloading(center)"
                    >
                      {{ isCenterExcelDownloading(center) ? 'Exporting...' : 'Candidates Excel' }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-danger"
                      @click="exportCenterCandidatesPdf(center)"
                      :disabled="isCenterPdfDownloading(center)"
                    >
                      {{ isCenterPdfDownloading(center) ? 'Downloading...' : 'Candidates PDF' }}
                    </button>
                  </div>
                </div>

                <div class="meta-grid">
                  <div class="meta-row" v-if="getZoneName(center)">
                    <span class="meta-label mal-text">സോൺ</span>
                    <span class="meta-value">{{ getZoneName(center) }}</span>
                  </div>
                  <div class="meta-row" v-if="getUnitName(center)">
                    <span class="meta-label mal-text">യൂണിറ്റ്</span>
                    <span class="meta-value">{{ getUnitName(center) }}</span>
                  </div>
                  <div class="meta-row" v-if="showStateWideDistrictColumn && center.district_name">
                    <span class="meta-label mal-text">ജില്ല</span>
                    <span class="meta-value">{{ center.district_name }}</span>
                  </div>
                </div>

                <div class="divider"></div>

                <div class="meta-grid">
                  <div class="meta-row" v-if="center.convenor_name">
                    <span class="meta-label mal-text">കൺവീനർ</span>
                    <span class="meta-value">{{ center.convenor_name }}</span>
                  </div>
                  <div class="meta-row" v-if="center.invigilator_name">
                    <span class="meta-label mal-text">ഇൻവിജിലേറ്റർ</span>
                    <span class="meta-value">{{ center.invigilator_name }}</span>
                  </div>
                  <div class="meta-row">
                    <span class="meta-label mal-text">ഫോൺ</span>
                    <span class="meta-value">
                      <a v-if="center.convenor_phone" :href="'tel:' + center.convenor_phone">{{ center.convenor_phone }}</a>
                      <template v-else>-</template>
                    </span>
                  </div>
                  <div class="meta-row" v-if="center.exam_time">
                    <span class="meta-label mal-text">സമയം</span>
                    <span class="meta-value">{{ center.exam_time || '' }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="empty-note mal-text">
        ഇതുവരെ പരീക്ഷാ കേന്ദ്രങ്ങൾ ചേർത്തിട്ടില്ല.
      </div>
    </template>
  </div>
</template>

<script>
import UserService from '../services/user.service';

/** Last 10 digits — same list as QhlsController::qhlsStateExamCenterListDelegatePhones() */
const QHLS_STATE_EXAM_CENTER_LIST_DELEGATE_PHONES = Object.freeze([
  '9567352527',
  '9037159419',
  '8075539521',
  '9746559284',
  '9746385256',
  '9746618986',
  '9747345746',
  '8129038430',
]);

export default {
  name: 'QHLSExamCenterList',
  data() {
    return {
      loading: false,
      savedCenters: [],
      madrasas: [],
      groupedMode: true,
      excelDownloading: false,
      centerExcelDownloading: {},
      centerPdfDownloading: {},
      // drill-down state
      districts: [],
      zones: [],
      units: [],
      drillStep: 'root', // state: district -> zone -> unit, district: zone -> unit, zone: unit
      selectedDistrict: null,
      selectedZone: null,
      selectedUnitKey: '',
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    subLevel() {
      const raw = this.$route.params.sub_level;
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? 0 : n;
    },
    zoneId() {
      if (this.subLevel !== 3) return null;
      const id = this.$route.params.id;
      if (id) return parseInt(id, 10);
      return null;
    },
    districtId() {
      if (this.subLevel !== 2) return null;
      const id = this.$route.params.id;
      if (id) return parseInt(id, 10);
      return null;
    },
    stateId() {
      if (this.subLevel !== 1) return null;
      const id = this.$route.params.id;
      if (id) return parseInt(id, 10);
      return null;
    },
    userLevel() {
      const raw = this.currentUser?.user_level;
      if (raw == null) return 0;
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? 0 : n;
    },
    isStateExamCenterListDelegate() {
      const raw =
        this.currentUser?.phone ??
        this.currentUser?.mobile ??
        this.currentUser?.username ??
        '';
      const digits = String(raw).replace(/\D/g, '');
      const last10 = digits.length >= 10 ? digits.slice(-10) : digits;
      return last10 !== '' && QHLS_STATE_EXAM_CENTER_LIST_DELEGATE_PHONES.includes(last10);
    },
    /** State-wide drill UI for delegates who are not state admins */
    effectiveSubLevel() {
      return this.isStateExamCenterListDelegate ? 1 : this.subLevel;
    },
    showStateWideDistrictColumn() {
      return this.userLevel === 1 || this.isStateExamCenterListDelegate;
    },
    currentMemberId() {
      const raw = this.currentUser?.member_id;
      if (raw == null || raw === '') return null;
      const n = parseInt(String(raw), 10);
      return Number.isNaN(n) ? null : n;
    },
    groupHeading() {
      if (this.effectiveSubLevel === 1) return 'District → Zone → Unit wise count';
      if (this.effectiveSubLevel === 2) return 'Zone → Unit wise count';
      if (this.effectiveSubLevel === 3) return 'Unit wise count';
      return 'Summary';
    },
    drillTitle() {
      if (this.effectiveSubLevel === 1) {
        if (this.drillStep === 'district') return 'District wise';
        if (this.drillStep === 'zone') return 'Zone wise';
        if (this.drillStep === 'unit') return 'Unit wise';
      }
      if (this.effectiveSubLevel === 2) {
        if (this.drillStep === 'zone') return 'Zone wise';
        if (this.drillStep === 'unit') return 'Unit wise';
      }
      if (this.effectiveSubLevel === 3) return 'Unit wise';
      return '';
    },
    canGoBack() {
      if (this.effectiveSubLevel === 1) return this.drillStep !== 'district';
      if (this.effectiveSubLevel === 2) return this.drillStep !== 'zone';
      return false;
    },
    drillCards() {
      if (this.effectiveSubLevel === 1) {
        if (this.drillStep === 'district') return this.getDistrictCards();
        if (this.drillStep === 'zone') return this.getZoneCards();
        if (this.drillStep === 'unit') return this.getUnitCards();
      }
      if (this.effectiveSubLevel === 2) {
        if (this.drillStep === 'zone') return this.getZoneCards();
        if (this.drillStep === 'unit') return this.getUnitCards();
      }
      if (this.effectiveSubLevel === 3) return this.getUnitCards();
      return [];
    },
    drillShowCenters() {
      // Show centers list only at the last step (units)
      return this.drillStep === 'unit';
    },
    filteredCenters() {
      if (!this.drillShowCenters) return [];
      // For unit step, we set selectedZone and also selectedUnitKey in drillSelect payload
      if (!this.selectedUnitKey) return [];
      return this.savedCenters.filter((c) => this.getUnitKey(c) === this.selectedUnitKey);
    },
    selectedUnitLabel() {
      if (!this.selectedUnitKey) return '';
      const u = (this.units || []).find((x) => String(x.id) === String(this.selectedUnitKey));
      return u ? u.name : 'Selected Unit';
    },
    canExportExcel() {
      // Requirement: state admins export district/zone/unit; district admins export zone/unit.
      return this.effectiveSubLevel === 1 || this.effectiveSubLevel === 2;
    },
  },
  mounted() {
    this.$root.updateParent('QHLS പരീക്ഷാ കേന്ദ്രങ്ങൾ');
    this.fetchSavedCenters();
  },
  watch: {
    '$route.params.id'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchSavedCenters();
      }
    },
  },
  methods: {
    isInvigilatorForCenter(center) {
      if (!center) return false;
      if (!this.currentMemberId) return false;
      const cid = parseInt(String(center.invigilator_member_id || ''), 10);
      return !Number.isNaN(cid) && cid === this.currentMemberId;
    },
    isCenterExcelDownloading(center) {
      const id = center?.id;
      return !!(id && this.centerExcelDownloading && this.centerExcelDownloading[id]);
    },
    isCenterPdfDownloading(center) {
      const id = center?.id;
      return !!(id && this.centerPdfDownloading && this.centerPdfDownloading[id]);
    },
    toggleGrouped() {
      this.groupedMode = !this.groupedMode;
      this.resetDrill();
    },
    resetDrill() {
      this.selectedDistrict = null;
      this.selectedZone = null;
      this.selectedUnitKey = '';
      if (this.effectiveSubLevel === 1) this.drillStep = 'district';
      else if (this.effectiveSubLevel === 2) this.drillStep = 'zone';
      else if (this.effectiveSubLevel === 3) this.drillStep = 'unit';
      else this.drillStep = 'root';
    },
    drillBack() {
      // If we are in unit step and a unit is selected, go back to unit cards first
      if (this.drillStep === 'unit' && this.selectedUnitKey) {
        this.selectedUnitKey = '';
        return;
      }
      if (this.effectiveSubLevel === 1) {
        if (this.drillStep === 'unit') {
          this.selectedUnitKey = '';
          this.drillStep = 'zone';
          return;
        }
        if (this.drillStep === 'zone') {
          this.selectedZone = null;
          this.zones = [];
          this.drillStep = 'district';
          return;
        }
      }
      if (this.effectiveSubLevel === 2) {
        if (this.drillStep === 'unit') {
          this.selectedUnitKey = '';
          this.drillStep = 'zone';
          return;
        }
      }
    },
    async drillSelect(g) {
      // State: district -> zone -> unit
      if (this.effectiveSubLevel === 1) {
        if (this.drillStep === 'district') {
          this.selectedDistrict = g;
          await this.fetchZonesForDistrict(g.id);
          this.drillStep = 'zone';
          return;
        }
        if (this.drillStep === 'zone') {
          this.selectedZone = g;
          await this.fetchUnitsForZone(g.id);
          this.drillStep = 'unit';
          return;
        }
        if (this.drillStep === 'unit') {
          this.selectedUnitKey = g.key;
          return;
        }
      }

      // District: zone -> unit
      if (this.effectiveSubLevel === 2) {
        if (this.drillStep === 'zone') {
          this.selectedZone = g;
          await this.fetchUnitsForZone(g.id);
          this.drillStep = 'unit';
          return;
        }
        if (this.drillStep === 'unit') {
          this.selectedUnitKey = g.key;
        }
      }

      // Zone: unit
      if (this.effectiveSubLevel === 3 && this.drillStep === 'unit') {
        this.selectedUnitKey = g.key;
      }
    },
    getDistrictCards() {
      return this.districts.map((d) => {
        const key = String(d.id);
        const count = this.savedCenters.filter((c) => String(this.getDistrictKey(c) ?? '') === key).length;
        return { key, id: d.id, label: d.name, count };
      });
    },
    getZoneCards() {
      return this.zones.map((z) => {
        const key = String(z.id);
        const count = this.savedCenters.filter((c) => String(this.getZoneKey(c) ?? '') === key).length;
        return { key, id: z.id, label: z.name, count };
      });
    },
    getUnitCards() {
      return this.units.map((u) => {
        const key = String(u.id);
        const count = this.savedCenters.filter((c) => String(this.getUnitId(c) ?? '') === key).length;
        return { key, id: u.id, label: u.name, count };
      });
    },
    async fetchSavedCenters() {
      this.loading = true;
      this.savedCenters = [];
      this.resetDrill();
      const delegate = this.isStateExamCenterListDelegate;
      try {
        if (!delegate && this.subLevel == 3 && this.zoneId) {
          const response = await UserService.authGetRequest('qhls-exam-centers', {
            zone_id: this.zoneId,
          });
          if (response.data.status === 'success' && Array.isArray(response.data.data?.list)) {
            this.savedCenters = response.data.data.list;
          }
          await this.fetchUnitsForZone(this.zoneId);
          this.drillStep = 'unit';
          return;
        }

        if (!delegate && this.subLevel == 2 && this.districtId) {
          const response = await UserService.authGetRequest('qhls-exam-centers', {
            district_id: this.districtId,
          });
          if (response.data.status === 'success' && Array.isArray(response.data.data?.list)) {
            this.savedCenters = response.data.data.list;
          }
          await this.fetchZonesForDistrict(this.districtId);
          this.drillStep = 'zone';
          return;
        }

        if (delegate || this.subLevel == 1) {
          const response = await UserService.authGetRequest('qhls-exam-centers', {});
          if (response.data.status === 'success' && Array.isArray(response.data.data?.list)) {
            this.savedCenters = response.data.data.list;
          }
          await this.fetchDistricts();
          this.drillStep = 'district';
        }
      } catch (err) {
        console.warn('Could not load saved centers:', err);
        this.savedCenters = [];
      } finally {
        this.loading = false;
      }
    },
    async exportExcel() {
      this.excelDownloading = true;
      try {
        const payload = {};
        if (this.effectiveSubLevel === 2 && this.districtId) {
          payload.district_id = this.districtId;
        }

        const response = await UserService.authPostRequest('export-qhls-exam-centers-report-excel', payload, {
          responseType: 'blob',
        });

        const blob = response.data;
        const contentType = (response.headers && response.headers['content-type']) || '';
        if (contentType.indexOf('application/json') !== -1) {
          const text = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsText(blob);
          });
          const json = JSON.parse(text);
          alert(json.message || 'Download failed');
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const scope = this.effectiveSubLevel === 2 ? `district-${this.districtId}` : 'state';
        a.download = `qhls-exam-centers-report-${scope}-${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        let msg = 'Failed to export Excel';
        if (err?.response?.data && typeof err.response.data === 'object' && !(err.response.data instanceof Blob)) {
          msg = err.response.data.message || msg;
        } else if (err?.message) {
          msg = err.message;
        }
        alert(msg);
      } finally {
        this.excelDownloading = false;
      }
    },
    async exportCenterCandidatesExcel(center) {
      if (!center?.id) return;
      const id = center.id;
      this.$set(this.centerExcelDownloading, id, true);
      try {
        const response = await UserService.authPostRequest(
          'qhls-exam-center-candidates-excel',
          { center_id: id },
          { responseType: 'blob' }
        );

        const blob = response.data;
        const contentType = (response.headers && response.headers['content-type']) || '';
        if (contentType.indexOf('application/json') !== -1) {
          const text = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsText(blob);
          });
          const json = JSON.parse(text);
          alert(json.message || 'Download failed');
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qhls-center-candidates-${id}-${new Date().toISOString().slice(0, 10)}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        let msg = 'Failed to export Excel';
        if (err?.response?.data && typeof err.response.data === 'object' && !(err.response.data instanceof Blob)) {
          msg = err.response.data.message || msg;
        } else if (err?.message) {
          msg = err.message;
        }
        alert(msg);
      } finally {
        this.$set(this.centerExcelDownloading, id, false);
      }
    },
    async exportCenterCandidatesPdf(center) {
      if (!center?.id) return;
      const id = center.id;
      this.$set(this.centerPdfDownloading, id, true);
      try {
        const response = await UserService.authPostRequest(
          'qhls-exam-center-candidates-pdf',
          { center_id: id },
          { responseType: 'blob' }
        );

        const blob = response.data;
        const contentType = (response.headers && response.headers['content-type']) || '';
        if (contentType.indexOf('application/json') !== -1) {
          const text = await new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsText(blob);
          });
          const json = JSON.parse(text);
          alert(json.message || 'Download failed');
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qhls-center-candidates-${id}-${new Date().toISOString().slice(0, 10)}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        let msg = 'Failed to download PDF';
        if (err?.response?.data && typeof err.response.data === 'object' && !(err.response.data instanceof Blob)) {
          msg = err.response.data.message || msg;
        } else if (err?.message) {
          msg = err.message;
        }
        alert(msg);
      } finally {
        this.$set(this.centerPdfDownloading, id, false);
      }
    },
    async fetchDistricts() {
      // Org districts (same as Org.vue state view)
      try {
        const res = await UserService.getSubLevels('get-sub-levels', { sub_level_type: 2, id: 0 });
        this.districts = res.data?.data?.list || [];
      } catch (e) {
        this.districts = [];
      }
    },
    async fetchZonesForDistrict(districtId) {
      this.zones = [];
      this.units = [];
      this.selectedZone = null;
      this.selectedUnitKey = '';
      try {
        const res = await UserService.getSubLevels('get-sub-levels', { sub_level_type: 3, id: districtId });
        this.zones = res.data?.data?.list || [];
      } catch (e) {
        this.zones = [];
      }
    },
    async fetchUnitsForZone(zoneId) {
      this.units = [];
      this.selectedUnitKey = '';
      try {
        const res = await UserService.getSubLevels('get-sub-levels', { sub_level_type: 4, id: zoneId });
        this.units = res.data?.data?.list || [];
      } catch (e) {
        this.units = [];
      }
    },
    getDistrictKey(center) {
      return center?.district_id ?? center?.districtId ?? center?.district?.id ?? center?.org_district_id ?? null;
    },
    getZoneKey(center) {
      return center?.zone_id ?? center?.zoneId ?? center?.zone?.id ?? center?.org_zone_id ?? null;
    },
    getUnitId(center) {
      return center?.unit_id ?? center?.unitId ?? center?.unit?.id ?? center?.center_unit_id ?? center?.org_unit_id ?? null;
    },
    getUnitKey(center) {
      const uid = this.getUnitId(center);
      return uid != null ? String(uid) : (this.getUnitName(center) || '');
    },
    formatDate(d) {
      if (!d) return '';
      const dt = new Date(d + 'T00:00:00');
      if (isNaN(dt.getTime())) return d;
      const day = String(dt.getDate()).padStart(2, '0');
      const month = String(dt.getMonth() + 1) + '';
      const year = dt.getFullYear();
      return `${day}-${month.padStart(2, '0')}-${year}`;
    },
    getCenterName(center) {
      if (center.madrasa_name) return center.madrasa_name;
      if (center.madrasa && center.madrasa.name) return center.madrasa.name;
      const m = this.madrasas.find((x) => x.id === center.madrasa_id || x.id == center.madrasa_id);
      return (m && m.name) ? m.name : (center.convenor_name || '');
    },
    getZoneName(center) {
      if (!center) return null;
      const z = center.zone_name ?? center.zoneName ?? center.zone?.name ?? center.zone?.zone_name ?? center.zone_title;
      return typeof z === 'string' && z.trim() ? z : null;
    },
    getUnitName(center) {
      if (!center) return null;
      const candidate =
        center.unit_name ??
        center.unitName ??
        (typeof center.unit === 'string' ? center.unit : null) ??
        center.unit?.name ??
        center.unit?.unit_name ??
        center.unit?.title ??
        center.unit_title ??
        center.center_unit_name ??
        center.centerUnitName ??
        center.center?.unit_name ??
        center.center?.unit?.name ??
        center.centre_unit_name ??
        null;

      if (typeof candidate === 'string' && candidate.trim()) return candidate;

      const uid = center.unit_id ?? center.center_unit_id ?? center.unitId ?? null;
      if (uid != null && String(uid).trim() !== '') return `#${uid}`;

      return null;
    },
  },
};
</script>

<style scoped>
.qhls-exam-center-list {
  max-width: 480px;
  margin: 0 auto;
  padding: 16px;
}
.page-header {
  margin-bottom: 20px;
}
.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a418c;
  margin-bottom: 4px;
}
.page-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
}
.readonly-banner {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 20px;
  line-height: 1.5;
}
.saved-centers-section {
  margin-top: 8px;
}
.section-title {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 12px;
  color: #1a418c;
}
.saved-centers-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.grouped-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.grouped-title {
  font-size: 0.95rem;
  color: #1a418c;
  font-weight: 600;
}
.grouped-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.group-card {
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.group-name {
  font-weight: 700;
  color: #111827;
  font-size: 1rem;
}
.group-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.group-count {
  font-size: 1.25rem;
  font-weight: 800;
  color: #1a418c;
  line-height: 1.1;
}
.group-count-label {
  font-size: 0.78rem;
  color: #6c757d;
}
.group-detail {
  margin-top: 6px;
  padding-top: 6px;
}
.group-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin: 6px 0 10px;
}
.group-detail-title {
  font-weight: 700;
  color: #1a418c;
}
.center-row {
  display: flex;
  align-items: stretch;
  gap: 10px;
}
.saved-center-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  align-items: stretch;
  gap: 12px;
  border: 1px solid #e9ecef;
  flex: 1;
}
.center-index {
  width: 36px;
  min-width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #1a418c;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin-top: 2px;
}
.center-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}
.center-info .center-name {
  font-size: 1rem;
  color: #1a418c;
}
.center-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.center-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.meta-grid {
  display: grid;
  gap: 6px;
}
.meta-row {
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 10px;
  align-items: start;
}
.meta-label {
  font-size: 0.85rem;
  color: #6c757d;
}
.meta-value {
  font-size: 0.92rem;
  color: #111827;
  word-break: break-word;
}
.meta-value a {
  color: #1a418c;
  text-decoration: none;
}
.divider {
  height: 1px;
  background: #e9ecef;
  margin: 6px 0;
}
.empty-note {
  margin-top: 18px;
  text-align: center;
  color: #6c757d;
}
</style>
